import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportClientError } from '../lib/reportError';

/**
 * The last thing between a thrown render and a white page.
 *
 * React still offers no hook for this in 19 — `getDerivedStateFromError` and `componentDidCatch`
 * are class-only — so this is a class, and the only one in the codebase.
 *
 * Two of them are mounted. The outer one wraps the whole tree and catches anything, including a
 * crash in the shell itself. The inner one sits inside the signed-in layout, so a page that throws
 * loses the page and keeps the navigation: the user can go somewhere else instead of reloading
 * into the same broken route.
 */

/**
 * The fallback's own strings, resolved from the document language rather than from i18next.
 *
 * Everything else in this app goes through `t()`. This does not, on purpose: the screen whose job
 * is to survive the app breaking should not depend on the app's own machinery to render a sentence.
 * If i18next were the thing that threw, a `t()` call here would throw again — and an error thrown
 * inside a fallback escalates past this boundary to the next one up, which for the outer boundary
 * is nothing at all. Two languages, six strings; the duplication buys a guarantee.
 */
const COPY = {
  bg: {
    title: 'Нещо се обърка',
    body: 'Възникна грешка в тази част от приложението. Изпратихме сигнал и ще я разгледаме. Данните ви са запазени.',
    retry: 'Опитай отново',
    reload: 'Презареди страницата',
    home: 'Към началото',
  },
  en: {
    title: 'Something went wrong',
    body: 'This part of the app hit an error. A report has been sent and we will look at it. Your data is safe.',
    retry: 'Try again',
    reload: 'Reload the page',
    home: 'Go to the start',
  },
} as const;

function copy() {
  return document.documentElement.lang === 'en' ? COPY.en : COPY.bg;
}

interface Props {
  children: ReactNode;
  /**
   * Reset the boundary when this changes — the route path, for the inner one. Without it, a user
   * who navigates away from a crashed page arrives at a different route still showing the fallback,
   * because the boundary has no idea anything changed.
   */
  resetKey?: string;
  /** The outer boundary has no navigation around it, so it offers a way back to the start. */
  showHomeLink?: boolean;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  // `onCaughtError` on the root reports this too, and `reportClientError` de-duplicates by
  // message, so only one of the two is ever sent. Both exist because they fail differently: the
  // root option catches what a boundary swallows anywhere in the tree, and this catches it even if
  // the root is ever constructed without the options.
  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError(error, 'boundary', info.componentStack);
  }

  componentDidUpdate(previous: Props) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private retry = () => this.setState({ error: null });

  private reload = () => window.location.reload();

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const text = copy();

    return (
      <div className="crash" role="alert">
        <h1 className="crash__title">{text.title}</h1>
        <p className="crash__body">{text.body}</p>
        <div className="crash__actions">
          <button type="button" className="btn btn--primary" onClick={this.retry}>
            {text.retry}
          </button>
          <button type="button" className="btn" onClick={this.reload}>
            {text.reload}
          </button>
          {this.props.showHomeLink && (
            // A plain anchor, not a Link: the router is one of the things that might have thrown.
            <a className="btn" href="/">
              {text.home}
            </a>
          )}
        </div>
      </div>
    );
  }
}
